<template>
  <div id="app">
    <CesiumScene ref="cesiumScene" />

    <TaskGraphPanel 
      v-if="taskGraphVisible" 
      :graph-data="taskGraphData" 
      @close="taskGraphVisible = false" 
    />

    <BusinessOverlayLayer>
      <template #bottom-left-top>
        <TerminalStatusDock
          :status="terminalStatus"
          :build-info="buildInfo"
          :discovery-state="discoveryState"
          :discovery-error="discoveryError"
          @retry="retryDiscovery"
        />
      </template>

      <template #bottom-left-bottom>
        <ConnectionDiagnostics
          :diagnostics="connectionDiagnostics"
          :manual-busy="manualConnectionBusy"
          @manual-connect="connectManualRock5a"
          @retry-discovery="retryDiscovery"
        />
      </template>
    </BusinessOverlayLayer>

    <SmartOrb
      @command="handleCommand"
    />

    <VirtualCursor
      v-if="gestureReady"
      :visible="cursorState.controlEnabled && !cursorState.inactive"
      :x="cursorX"
      :y="cursorY"
      :label="cursorLabel"
      :is-clicking="cursorState.clicking"
      :is-scrolling="cursorState.scrolling"
      :is-snapped="cursorState.snapped"
      :is-fisting="cursorState.fisting"
      :is-inactive="cursorState.inactive"
      :control-enabled="cursorState.controlEnabled"
    />

    <ReservoirTargetOverlay
      :interaction="reservoirInteraction"
      :voice="multimodalState.voice"
      @unlock="_unlockReservoirTarget"
    />

    <div
      v-if="gestureReady"
      class="gesture-status-indicator"
      :class="{ active: cursorState.controlEnabled }"
    >
      <span class="status-dot"></span>
      <span class="status-text">
        {{ cursorState.controlEnabled ? '手势控制已激活' : '手势就绪 · 竖拇指激活' }}
      </span>
    </div>

    <!-- 仅在开启诊断或开发调试时展示 -->
    <div v-if="debugOverlayVisible" class="multimodal-debug-overlay">
      <div class="debug-title">多模态目标吸附调试 (Ctrl+Shift+D)</div>
      <div><span>Pointer:</span> <b>{{ Math.round(cursorX) }}, {{ Math.round(cursorY) }}</b></div>
      <div><span>State:</span> <b :class="'state-' + reservoirInteraction.state">{{ reservoirInteraction.state }}</b></div>
      <div><span>Hover:</span> <b>{{ (reservoirInteraction.hoverTarget && (reservoirInteraction.hoverTarget.displayName || reservoirInteraction.hoverTarget.name)) || '无' }}</b></div>
      <div><span>Selected:</span> <b>{{ (reservoirInteraction.selectedTarget && (reservoirInteraction.selectedTarget.displayName || reservoirInteraction.selectedTarget.name)) || 'null' }}</b></div>
      <div><span>Gesture:</span> <b>{{ cursorLabel || '无' }}</b></div>
      <div><span>Control:</span> <b>{{ cursorState.controlEnabled ? 'ENABLED' : 'DISABLED' }}</b></div>
    </div>

    <transition name="fade">
      <div v-if="gestureToast" class="gesture-toast">{{ gestureToast }}</div>
    </transition>
  </div>
</template>

<script>
import CesiumScene from './components/CesiumScene.vue'
import BusinessOverlayLayer from './components/layout/BusinessOverlayLayer.vue'
import SmartOrb from './components/SmartOrb.vue'
import TerminalStatusDock from './components/TerminalStatusDock.vue'
import ConnectionDiagnostics from './components/ConnectionDiagnostics.vue'
import VirtualCursor from './components/VirtualCursor.vue'
import ReservoirTargetOverlay from './components/ReservoirTargetOverlay.vue'
import TaskGraphPanel from './components/TaskGraphPanel.vue'
import { VoiceService } from './services/voiceService.js'
import { GestureService } from './services/gestureService.js'
import { LLMService } from './services/llmService.js'
import { getRock5aDeviceSession, getRock5aPairToken } from './services/rock5aDeviceSession.js'
import { discoverDevices } from './utils/lan_discovery.js'
import {
  getRock5aConnectionContextStore,
  probeRock5aConnectionContext,
  ROCK5A_CONNECTION_MODES,
} from './services/rock5aConnectionContext.js'
import { Rock5aConnectionCoordinator } from './services/rock5aConnectionCoordinator.js'
import {
  connectAssistantClient,
  createAssistantContextReporter,
  disconnectAssistantClient,
  getAssistantClient,
  getAssistantDiagnostics,
} from './services/assistantClient.js'
import { registerCompetitionActionsOnce } from './services/assistantActions.js'
import { GesturePointerBinding } from './services/gesturePointerBinding.js'
import { ReservoirTargetResolver } from './services/reservoirTargetResolver.js'
import { multimodalContext } from './services/multimodalContext.js'
import { fetchBuildInfo } from './utils/buildInfo.js'

export default {
  name: 'App',
  components: {
    CesiumScene,
    BusinessOverlayLayer,
    SmartOrb,
    TerminalStatusDock,
    ConnectionDiagnostics,
    VirtualCursor,
    ReservoirTargetOverlay,
    TaskGraphPanel,
  },
  provide() {
    return {
      voiceService: this.voice,
      gestureService: this.gesture,
      llmService: this.llm,
      terminalSession: this.terminalSession,
      assistantClient: this.assistant,
      multimodalContextStore: multimodalContext,
    }
  },
  data() {
    const terminalSession = getRock5aDeviceSession()
    const connectionContextStore = getRock5aConnectionContextStore()
    return {
      terminalSession,
      connectionContextStore,
      connectionContext: connectionContextStore.current,
      voice: new VoiceService({ session: terminalSession }),
      gesture: new GestureService({ session: terminalSession }),
      llm: new LLMService(),
      assistant: getAssistantClient(),
      multimodalContextStore: multimodalContext,
      assistantConnected: false,
      terminalStatus: terminalSession.getStatus(),
      discoveryState: 'idle',
      discoveryError: '',
      helperHealthy: false,
      manualConnectionBusy: false,
      buildInfo: { version: 'unknown', commit: 'unknown', builtAt: '' },
      activeDeviceKey: '',
      cursorX: window.innerWidth / 2,
      cursorY: window.innerHeight / 2,
      cursorLabel: '',
      cursorState: {
        clicking: false,
        scrolling: false,
        snapped: false,
        fisting: false,
        inactive: true,
        controlEnabled: false,
      },
      gestureToast: '',
      taskGraphVisible: false,
      taskGraphData: null,
      debugOverlayVisible: false,
      reservoirInteraction: {
        state: 'FREE',
        hoverTarget: null,
        selectedTarget: null,
        snapped: false,
        locked: false,
        screenPosition: null,
      },
      multimodalState: multimodalContext.snapshot(),
    }
  },
  computed: {
    connectionDiagnostics() {
      const context = this.connectionContext
      const assistant = getAssistantDiagnostics()
      return {
        pageUrl: window.location.href,
        pageProtocol: window.location.protocol,
        device: context ? `${context.deviceName} (${context.host})` : '',
        discoverySource: context && context.discoverySource,
        helperHealth: this.helperHealthy ? 'healthy' : 'unavailable / 可手动连接',
        control: this.terminalStatus.control || 'offline',
        audio: this.terminalStatus.audio || 'idle',
        assistant: this.assistantConnected ? 'connected' : assistant.connectionState,
        assistantUrl: assistant.url,
        clientRole: assistant.clientRole,
        sessionId: assistant.sessionId,
        buildInfo: `${this.buildInfo.buildTarget || 'unknown'} / ${this.buildInfo.commit || this.buildInfo.gitCommit || 'unknown'}`,
        runtimeEnabled: process.env.VUE_APP_COMPETITION_RUNTIME_ENABLED === 'true',
        tokenState: context && context.token ? 'configured' : 'missing',
        demoFloodMode: process.env.VUE_APP_FLOOD_DEMO_MODE === 'true',
        browserBlocked: assistant.browserBlocked,
        closeCode: [
          `control=${this.terminalStatus.closeCode?.control || '-'}`,
          `audio=${this.terminalStatus.closeCode?.audio || '-'}`,
          `assistant=${assistant.closeCode || '-'}`,
        ].join(', '),
        lastError: this.discoveryError || assistant.lastError || this.terminalStatus.error?.control || '',
      }
    },
    gestureReady() {
      if (this.terminalStatus.control !== 'connected') return false
      const terminalGesture = this.terminalStatus.terminalStatus
        && this.terminalStatus.terminalStatus.gesture
      const gestureFresh = this.terminalStatus.freshness
        && this.terminalStatus.freshness.gesture
        && this.terminalStatus.freshness.gesture.fresh
      return Boolean((terminalGesture && terminalGesture.running) || gestureFresh)
    },
  },
  watch: {
    '$route.fullPath'() {
      if (this._gesturePointerBinding) this._gesturePointerBinding.clear('route_changed', true)
      this._reservoirResolver.clear()
      this.reservoirInteraction = this._reservoirResolver.snapshot()
      multimodalContext.clearTarget()
      this._reportContextToAssistant()
    },
  },
  created() {
    // Vue 2 intentionally does not proxy data keys that start with `_`.
    // Keep non-reactive runtime handles on the component instance instead.
    Object.assign(this, {
      _gestureToastTimer: null,
      _discoveryTimer: null,
      _cesiumTimer: null,
      _terminalUnsubscribe: null,
      _connectionContextUnsubscribe: null,
      _connectionCoordinator: null,
      _gestureUnsubscribe: null,
      _gestureActionHandler: null,
      _assistantContextTimer: null,
      _assistantContextReporter: null,
      _assistantRegistration: null,
      _assistantUnsubscribers: [],
      _gesturePointerBinding: null,
      _destroyed: false,
      _reservoirResolver: new ReservoirTargetResolver({
        getTargets: () => {
          const scene = this.$refs && this.$refs.cesiumScene
          return scene && typeof scene.getReservoirScreenTargets === 'function'
            ? scene.getReservoirScreenTargets()
            : []
        },
      }),
      _multimodalUnsubscribe: null,
      _openPalmSince: 0,
    })
  },
  mounted() {
    this._loadBuildInfo()
    this._terminalUnsubscribe = this.terminalSession.subscribe('status', this._onTerminalStatus)
    this._multimodalUnsubscribe = multimodalContext.subscribe((state) => {
      this.multimodalState = state
      if (this._assistantContextReporter) this._reportContextToAssistant()
    })
    this._connectionContextUnsubscribe = this.connectionContextStore.subscribe((context) => {
      this.connectionContext = context
    })
    this.$root.$on('toggle-task-graph', () => {
      this.taskGraphVisible = !this.taskGraphVisible
    })
    this.$root.$on('task-graph-update', (taskData) => {
      this.taskGraphData = taskData
      this.taskGraphVisible = true
    })
    this.$root.$on('unlock-reservoir-target', () => {
      this._unlockReservoirTarget()
    })
    this._onKeyDownHandler = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        this.debugOverlayVisible = !this.debugOverlayVisible
      }
    }
    window.addEventListener('keydown', this._onKeyDownHandler)
    this.$root.$on('simulate-flood', (args) => {
      if (this.$refs.cesiumScene && this.$refs.cesiumScene.autoStartFloodPreview) {
         this.$refs.cesiumScene.autoStartFloodPreview({ autoRunSchemeNum: args.schemeNum || 2 })
      }
    })
    this.terminalSession.setRediscoverHook(async () => await discoverDevices())
    this._registerGestureCallbacks()
    this._waitForCesium()
    this._initAssistant()
    this._connectionCoordinator = new Rock5aConnectionCoordinator({
      contextStore: this.connectionContextStore,
      gesture: this.gesture,
      voice: this.voice,
      connectAssistant: connectAssistantClient,
      disconnectAssistant: disconnectAssistantClient,
    })
    this.retryDiscovery()
  },
  methods: {
    async _loadBuildInfo() {
      try {
        this.buildInfo = await fetchBuildInfo()
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('无法读取前端 build-info.json', error)
        }
      }
    },
    async retryDiscovery() {
      if (this._destroyed || this.discoveryState === 'searching') return
      if (this._discoveryTimer) {
        clearTimeout(this._discoveryTimer)
        this._discoveryTimer = null
      }
      this.discoveryState = 'searching'
      this.discoveryError = ''

      try {
        const result = await discoverDevices()
        if (this._destroyed) return
        const devices = result && Array.isArray(result.devices) ? result.devices : []
        if (result && result.found && devices.length > 0) {
          this.helperHealthy = true
          this.discoveryState = 'found'
          await this._connectDevice(this._selectDevice(devices), ROCK5A_CONNECTION_MODES.DIRECT_LAN)
          return
        }
        this.helperHealthy = Boolean(result && result.error !== 'helper_unavailable')
        this.discoveryState = result && result.error === 'helper_unavailable'
          ? 'manual-required'
          : 'not-found'
        this.discoveryError = (result && result.error) || '未发现 Rock5A 终端'
      } catch (error) {
        if (this._destroyed) return
        this.discoveryState = 'error'
        this.discoveryError = error && error.message ? error.message : '终端发现失败'
      }

      // Stay interactive. The diagnostic panel offers explicit retry and manual IP fallback.
    },
    _selectDevice(devices) {
      return devices.find(device => device && device.ip && device.status !== 'offline')
        || devices.find(device => device && device.ip)
        || devices[0]
    },
    async _connectDevice(device, discoverySource) {
      if (!device) {
        this.discoveryError = '发现结果缺少终端地址'
        return
      }
      const key = `${device.id || device.hostname || device.name || ''}@${device.ip || device.host || ''}:${device.port || 8770}`
      if (key === this.activeDeviceKey && this.terminalStatus.control !== 'offline') return

      this.activeDeviceKey = key
      try {
        const context = this.connectionContextStore.select(device, {
          discoverySource,
          pageProtocol: window.location.protocol,
          token: getRock5aPairToken(device),
        })
        await this._connectionCoordinator.connect(context)
      } catch (error) {
        this.discoveryState = 'error'
        this.discoveryError = error.message || 'Rock5A connection failed'
      }
    },
    async connectManualRock5a(device) {
      if (this.manualConnectionBusy) return
      this.manualConnectionBusy = true
      this.discoveryError = ''
      try {
        const context = this.connectionContextStore.select(device, {
          discoverySource: ROCK5A_CONNECTION_MODES.MANUAL_IP,
          pageProtocol: window.location.protocol,
          saveRecent: true,
          token: getRock5aPairToken(device),
        })
        const probe = await probeRock5aConnectionContext(context, {
          pageProtocol: window.location.protocol,
          timeoutMs: 3000,
        })
        if (!probe.ok) {
          const error = new Error(`Rock5A reachability failed: control=${probe.control.reason || 'ok'}, audio=${probe.audio.reason || 'ok'}`)
          error.code = 'ROCK5A_UNREACHABLE'
          throw error
        }
        this.discoveryState = 'found'
        await this._connectionCoordinator.connect(context)
      } catch (error) {
        this.discoveryState = 'manual-required'
        this.discoveryError = error.message || 'Manual Rock5A connection failed'
      } finally {
        this.manualConnectionBusy = false
      }
    },
    _onTerminalStatus(status) {
      this.terminalStatus = status || this.terminalSession.getStatus()
      const controlFresh = this.terminalStatus.freshness
        && this.terminalStatus.freshness.control
        && this.terminalStatus.freshness.control.fresh
      if (this.terminalStatus.control === 'connected' && controlFresh) {
        this.discoveryState = 'connected'
        this.discoveryError = ''
      }
    },
    _registerGestureCallbacks() {
      const unsubscribe = this.gesture.onGesture((frame) => {
        const targetResult = this._handleReservoirFrame(frame)
        const screenPosition = targetResult.screenPosition || { x: frame.x, y: frame.y }
        this.cursorX = screenPosition.x
        this.cursorY = screenPosition.y
        this.cursorLabel = this.gesture._gestureName
        this.cursorState = {
          ...this.gesture.cursorClass,
          snapped: Boolean(targetResult.snapped || targetResult.locked),
        }
        if (this._gesturePointerBinding) {
          const target = targetResult.hoverTarget || targetResult.selectedTarget
          this._gesturePointerBinding.handleFrame({
            ...frame,
            x: screenPosition.x,
            y: screenPosition.y,
            resolvedTarget: target ? {
              objectId: String(target.objectId || target.entityId || target.businessId || ''),
              objectType: 'reservoir',
              displayName: target.displayName || target.name,
              businessId: target.businessId,
            } : null,
          })
        }
      })
      if (typeof unsubscribe === 'function') this._gestureUnsubscribe = unsubscribe

      this._gestureActionHandler = (action) => {
        if (action === 'fist_hold') this._lockReservoirTarget()
        const toastMap = {
          fist_hold: '握拳 · 唤醒语音',
          voice_toggle: '语音 · 开始/停止聆听',
          voice_activate: '双手 · 唤醒语音',
          confirm_send: '竖拇指 · 确认',
          confirm: 'OK · 确认',
          bye: '挥手 · 返回',
          exit_system: '双手握拳长按 · 退出',
          scale_drag: '双手捏合 · 缩放拖拽',
        }
        if (toastMap[action]) this._showGestureToast(toastMap[action])
      }
      this.gesture.onAction(this._gestureActionHandler)
    },
    _waitForCesium() {
      let attempts = 0
      this._cesiumTimer = setInterval(() => {
        attempts += 1
        if (window.viewer) {
          this.gesture.bindCesiumViewer(window.viewer)
          this.gesture.enable3DControl(true)
          this._startAssistantContextReporting()
          clearInterval(this._cesiumTimer)
          this._cesiumTimer = null
        } else if (attempts >= 120) {
          clearInterval(this._cesiumTimer)
          this._cesiumTimer = null
        }
      }, 500)
    },
    _handleReservoirFrame(frame = {}) {
      const result = this._reservoirResolver.update({
        x: frame.x,
        y: frame.y,
        timestamp: frame.timestamp,
        active: frame.pointerActive !== false,
        controlEnabled: frame.controlEnabled !== false,
      })
      this.reservoirInteraction = result
      const scene = this.$refs.cesiumScene
      const hover = result.hoverTarget
      const selected = result.selectedTarget
      if (scene && typeof scene.setReservoirInteractionState === 'function') {
        scene.setReservoirInteractionState(hover || selected, {
          hovered: Boolean(hover),
          locked: Boolean(selected),
          selectedTarget: selected,
        })
      }
      multimodalContext.setHoverTarget(hover)

      if (selected && frame.code === 1 && Number(frame.confidence || 0) >= 0.55) {
        if (!this._openPalmSince) this._openPalmSince = Date.now()
        if (Date.now() - this._openPalmSince >= 300) this._unlockReservoirTarget()
      } else {
        this._openPalmSince = 0
      }
      return result
    },
    _lockReservoirTarget() {
      const selected = this._reservoirResolver.lock()
      if (!selected) return false
      this.reservoirInteraction = this._reservoirResolver.snapshot()
      multimodalContext.lockTarget(selected, 'fist')
      const scene = this.$refs.cesiumScene
      if (scene && typeof scene.setReservoirInteractionState === 'function') {
        scene.setReservoirInteractionState(selected, { hovered: true, locked: true, selectedTarget: selected })
      }
      this._reportContextToAssistant()
      return true
    },
    _unlockReservoirTarget() {
      if (!this.reservoirInteraction.locked) return false
      this._reservoirResolver.unlock()
      this.reservoirInteraction = this._reservoirResolver.snapshot()
      multimodalContext.clearTarget()
      const scene = this.$refs.cesiumScene
      if (scene && typeof scene.setReservoirInteractionState === 'function') {
        scene.setReservoirInteractionState(this.reservoirInteraction.hoverTarget, {
          hovered: Boolean(this.reservoirInteraction.hoverTarget),
          locked: false,
          selectedTarget: null,
        })
      }
      this._reportContextToAssistant()
      return true
    },
    handleCommand(methodName) {
      const scene = this.$refs.cesiumScene
      if (scene && typeof scene[methodName] === 'function') {
        scene[methodName]()
      } else if (scene && scene.panelStore) {
        scene.panelStore.open(methodName)
      }
    },
    async _initAssistant() {
      this._gesturePointerBinding = new GesturePointerBinding({
        assistant: this.assistant,
        getScene: () => this.$refs.cesiumScene,
      })
      this._assistantRegistration = registerCompetitionActionsOnce(this.assistant, {
        getScene: () => this.$refs.cesiumScene,
      })
      this._assistantContextReporter = createAssistantContextReporter(this.assistant)
      if (typeof this.assistant.onConnect === 'function') {
        this._assistantUnsubscribers.push(this.assistant.onConnect(() => {
          this.assistantConnected = true
          this._reportContextToAssistant()
        }))
      }
      if (typeof this.assistant.onDisconnect === 'function') {
        this._assistantUnsubscribers.push(this.assistant.onDisconnect(() => {
          this.assistantConnected = false
        }))
      }
      if (typeof this.assistant.onConfirmationRequest === 'function') {
        this._assistantUnsubscribers.push(this.assistant.onConfirmationRequest((request) => {
          const summary = request && request.humanReadableSummary
          if (summary && this.voice) this.voice.speak(`${summary}。请说继续确认，或说取消。`)
        }))
      }
      if (typeof this.assistant.onTaskGraphUpdate === 'function') {
        this._assistantUnsubscribers.push(this.assistant.onTaskGraphUpdate((payload) => {
          this.taskGraphData = payload
        }))
      }
      if (typeof this.assistant.onStateChange === 'function') {
        this._assistantUnsubscribers.push(this.assistant.onStateChange((state, taskId, meta) => {
          if (meta && meta.rootTaskId && typeof this.assistant.syncTaskGraph === 'function') {
            this.assistant.syncTaskGraph(meta.rootTaskId)
          }
        }))
      }
      // The coordinator connects the already-registered singleton SDK only after
      // a Rock5aConnectionContext has been selected.
    },
    _reportContextToAssistant() {
      const scene = this.$refs.cesiumScene
      if (!scene || !this._assistantContextReporter) return
      const summary = scene.getAssistantContextSummary()
      this._assistantContextReporter.report({
        route: this.$route ? this.$route.fullPath : `${window.location.pathname}${window.location.hash || ''}`,
        selectedObject: summary.selectedObject,
        activePanel: summary.activePanel,
        activeLayers: summary.activeLayers,
        mapView: summary.mapView,
        multimodalTarget: this.multimodalState.target,
        hoverTarget: this.multimodalState.hoverTarget,
        voiceTargetContext: this.multimodalState.voice,
      })
    },
    _startAssistantContextReporting() {
      if (this._assistantContextTimer) return
      this._reportContextToAssistant()
      this._assistantContextTimer = setInterval(() => this._reportContextToAssistant(), 250)
    },
    _showGestureToast(text) {
      this.gestureToast = text
      if (this._gestureToastTimer) clearTimeout(this._gestureToastTimer)
      this._gestureToastTimer = setTimeout(() => { this.gestureToast = '' }, 2000)
    },
  },
  beforeDestroy() {
    this._destroyed = true
    if (this._terminalUnsubscribe) this._terminalUnsubscribe()
    if (this._connectionContextUnsubscribe) this._connectionContextUnsubscribe()
    if (this._gestureUnsubscribe) this._gestureUnsubscribe()
    if (this._gestureActionHandler && this.gesture) this.gesture.offAction(this._gestureActionHandler)
    if (this._discoveryTimer) clearTimeout(this._discoveryTimer)
    if (this._cesiumTimer) clearInterval(this._cesiumTimer)
    if (this._gestureToastTimer) clearTimeout(this._gestureToastTimer)
    if (this._assistantContextTimer) clearInterval(this._assistantContextTimer)
    if (this._assistantContextReporter) this._assistantContextReporter.dispose()
    if (this._gesturePointerBinding) this._gesturePointerBinding.dispose()
    if (this._multimodalUnsubscribe) this._multimodalUnsubscribe()
    if (this._onKeyDownHandler) {
      window.removeEventListener('keydown', this._onKeyDownHandler)
    }
    this._assistantUnsubscribers.forEach(unsubscribe => unsubscribe())
    if (this._assistantRegistration) this._assistantRegistration.unregister()
    if (this._connectionCoordinator) this._connectionCoordinator.disconnect()
    else disconnectAssistantClient()
  },
}
</script>

<style>
html, body, #app {
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  font-family: "Microsoft YaHei", sans-serif;
  background: #0a0a0f;
}

.multimodal-debug-overlay {
  position: fixed;
  left: 20px;
  top: 90px;
  z-index: 10002;
  background: rgba(10, 15, 29, 0.88);
  border: 1px solid rgba(0, 242, 248, 0.4);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  border-radius: 6px;
  padding: 10px 14px;
  color: #c9d1d9;
  font-size: 12px;
  font-family: monospace;
  line-height: 1.6;
  pointer-events: none;
}
.multimodal-debug-overlay .debug-title {
  color: #00f2f8;
  font-weight: bold;
  margin-bottom: 6px;
  border-bottom: 1px solid rgba(0, 242, 248, 0.2);
  padding-bottom: 4px;
}
.multimodal-debug-overlay b {
  color: #fff;
}
.multimodal-debug-overlay b.state-SNAPPED {
  color: #00f2f8;
}
.multimodal-debug-overlay b.state-LOCKED {
  color: #65f6cf;
}

.gesture-status-indicator {
  position: fixed;
  right: 148px;
  bottom: 18px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.62);
  background: rgba(20, 20, 35, 0.86);
  backdrop-filter: blur(8px);
  font-size: 12px;
  white-space: nowrap;
  transition: all 0.3s ease;
}
.gesture-status-indicator.active {
  border-color: #00f2f8;
  color: #00f2f8;
  box-shadow: 0 0 12px rgba(0, 242, 248, 0.3);
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}
.gesture-status-indicator.active .status-dot {
  background: #00f2f8;
  box-shadow: 0 0 8px #00f2f8;
}

.gesture-toast {
  position: fixed;
  right: 148px;
  bottom: 58px;
  z-index: 10001;
  padding: 10px 24px;
  border: 1px solid #00f2f8;
  border-radius: 8px;
  color: #00f2f8;
  background: rgba(0, 242, 248, 0.15);
  backdrop-filter: blur(12px);
  font-size: 15px;
  font-weight: 500;
  pointer-events: none;
  white-space: nowrap;
}
.fade-enter-active,
.fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter,
.fade-leave-to { opacity: 0; }

@media (max-width: 760px) {
  .gesture-status-indicator,
  .gesture-toast { display: none; }
}
</style>
